import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MessageSquareText, Save, Search } from "lucide-react";
import toast from "react-hot-toast";
import axiosInstance from "../../utils/axiosInstance";

const roleLabels = {
  admin: "مدیر",
  editor: "ویرایشگر",
  viewer: "مشاهده‌گر",
  accountent: "حسابدار",
  client: "مشتری",
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  const message =
    data?.currency_allocation_user_ids ||
    data?.proforma_expiry_user_ids ||
    data?.detail ||
    data?.error;
  if (Array.isArray(message)) return message[0];
  return message || "ذخیره تنظیمات پیامک ناموفق بود.";
};

export default function AdminSmsNotificationsSection() {
  const [users, setUsers] = useState([]);
  const [allocationIds, setAllocationIds] = useState(new Set());
  const [expiryIds, setExpiryIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    axiosInstance
      .get("/notifications/sms-preferences/")
      .then((response) => {
        if (!active) return;
        const nextUsers = response.data?.users || [];
        setUsers(nextUsers);
        setAllocationIds(
          new Set(
            nextUsers
              .filter((user) => user.currency_allocation_enabled ?? user.enabled)
              .map((user) => user.id),
          ),
        );
        setExpiryIds(
          new Set(nextUsers.filter((user) => user.proforma_expiry_enabled).map((user) => user.id)),
        );
      })
      .catch(() => {
        if (active) toast.error("دریافت تنظیمات پیامک ناموفق بود.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      [user.username, user.email, user.phone_number, roleLabels[user.role]]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(term)),
    );
  }, [search, users]);

  const toggleUser = (user, event) => {
    if (!user.phone_valid) return;
    const setSelectedIds = event === "expiry" ? setExpiryIds : setAllocationIds;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(user.id)) next.delete(user.id);
      else next.add(user.id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await axiosInstance.put("/notifications/sms-preferences/", {
        currency_allocation_user_ids: Array.from(allocationIds),
        proforma_expiry_user_ids: Array.from(expiryIds),
      });
      const nextUsers = response.data?.users || [];
      setUsers(nextUsers);
      setAllocationIds(
        new Set(nextUsers.filter((user) => user.currency_allocation_enabled).map((user) => user.id)),
      );
      setExpiryIds(
        new Set(nextUsers.filter((user) => user.proforma_expiry_enabled).map((user) => user.id)),
      );
      toast.success("گیرندگان پیامک اعلان‌ها ذخیره شدند.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl" dir="rtl">
      <div className="border-b border-slate-200 pb-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
              <MessageSquareText className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">پیامک اعلان‌ها</h2>
              <p className="mt-1 text-sm text-slate-500">گیرندگان پیامک تخصیص ارز و انقضای ثبت سفارش</p>
            </div>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "در حال ذخیره" : "ذخیره گیرندگان"}
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative block w-full sm:max-w-sm">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="جستجوی نام، ایمیل یا شماره تماس"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pr-10 pl-3 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
          />
        </label>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
          <span>تخصیص ارز: {allocationIds.size} گیرنده</span>
          <span>انقضای ثبت سفارش: {expiryIds.size} گیرنده</span>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex min-h-48 items-center justify-center text-slate-500">
            <Loader2 className="ml-2 h-5 w-5 animate-spin" />
            در حال دریافت کاربران
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex min-h-40 items-center justify-center text-sm text-slate-500">کاربری پیدا نشد.</div>
        ) : (
          <div>
            <div className="hidden grid-cols-[minmax(0,1fr)_9rem_9rem] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600 sm:grid">
              <span>کاربر</span>
              <span className="text-center">تخصیص ارز</span>
              <span className="text-center">انقضای ثبت سفارش</span>
            </div>
            <div className="divide-y divide-slate-100">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_9rem_9rem] sm:items-center ${
                  user.phone_valid ? "hover:bg-slate-50" : "bg-slate-50/70"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{user.username || user.email}</span>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                      {roleLabels[user.role] || user.role}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-500">{user.email}</p>
                  <div className={`mt-1 text-xs ${user.phone_valid ? "text-slate-500" : "text-rose-600"}`} dir="ltr">
                    {user.phone_number || "بدون شماره تماس"}
                  </div>
                  {!user.phone_valid && user.phone_number ? (
                    <div className="mt-1 text-xs text-rose-600" dir="rtl">شماره موبایل نامعتبر</div>
                  ) : null}
                </div>
                <label className={`flex items-center justify-between gap-2 sm:justify-center ${user.phone_valid ? "cursor-pointer" : "cursor-not-allowed"}`}>
                  <span className="text-xs text-slate-600 sm:hidden">تخصیص ارز</span>
                  <input
                    type="checkbox"
                    checked={allocationIds.has(user.id)}
                    disabled={!user.phone_valid}
                    onChange={() => toggleUser(user, "allocation")}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
                <label className={`flex items-center justify-between gap-2 sm:justify-center ${user.phone_valid ? "cursor-pointer" : "cursor-not-allowed"}`}>
                  <span className="text-xs text-slate-600 sm:hidden">انقضای ثبت سفارش</span>
                  <input
                    type="checkbox"
                    checked={expiryIds.has(user.id)}
                    disabled={!user.phone_valid}
                    onChange={() => toggleUser(user, "expiry")}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
